import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { libraryViewModes } from '../libraryViewModes';
import { ImageFile } from '../models/imageFile';
import { RelatedImageSetViewDataModel } from '../models/relatedImageSetViewDataModel';
import { BreakpointService } from '../breakpoint.service';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-image-carousel',
  templateUrl: './image-carousel.component.html',
  styleUrls: ['./image-carousel.component.css']
})
export class ImageCarouselComponent implements OnInit, AfterViewInit {

  /** An array of all of the images within one particular bound image set */
  @Input() imageModels: ImageFile[] = [];
  /** An array of all the images all the related images for a certain record. */
  @Input() rsvImageModels: RelatedImageSetViewDataModel[] = [];
  /**Boolean to mark image carousel in RSV mode or not. */
  @Input() isRSVMode = false;

  @Input() isBoundImageSet = false;

  @Input() isLedgerSet = false;

  @Input() recentlyViewedImages: ImageFile[] = [];

  @Input() recentlyViewedImagesMode = false;

  /** The currently selected/highlighted image */
  @Input() selectedImage: ImageFile;
  /** The # of images displayed in the carousel */
  @Input() carouselSize = 35;
  /** Message to display when there are no images */
  @Input() noImageMSG = 'No Images';
  /** Replacing image carousel view below single image view image for mobile view. */
  @Input() showButtonsInsteadOfCarousel = false;
  /** Record Selection Emitter */
  @Output() recordSelected: EventEmitter<number | RelatedImageSetViewDataModel> = new EventEmitter<number | RelatedImageSetViewDataModel>();
  /** Output to launch load event for more image records */
  @Output() handleEndOfImageSet: EventEmitter<any> = new EventEmitter<number>();

  @Output() imageCarouselOpenEmitter: EventEmitter<any> = new EventEmitter<boolean>();

  @ViewChild('carousel', { static: false }) carousel: ElementRef;

  /** The first index of the thumbnails array to display within the carousel */
  imageCarouselFirstIndex: number;
  /** The last index of the thumbnails array to display within the carousel */
  imageCarouselLastIndex: number;
  /** The name field to display over the image. Changes when moving between bound images in a set. */
  displayName: string;
  /** Imports the LibraryViewModes enum. */
  libViewModes = libraryViewModes;

  mainDataContainer = this.isRSVMode? this.rsvImageModels : this.imageModels;

  imageCarouselArray = [];

  isHandset = false;

  currentSelectedImageIndex: number;

  private _subscription;

  private dragScrolling = false;

  private holdScrolling = false;
  private originalScroll = 0;

  private startPosition: number;

  private scrollDistance: number;

  constructor(private cd: ChangeDetectorRef, private breakpointSvc: BreakpointService) { }

  ngOnInit() {

    this._subscription = this.breakpointSvc.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );

    if (this.isHandset) {
        this.carouselSize = 2;
    }
    // eslint-disable-next-line max-len
    this.mainDataContainer = this.isRSVMode? this.rsvImageModels : this.recentlyViewedImagesMode ? this.recentlyViewedImages : this.imageModels;

    // eslint-disable-next-line max-len
    this.noImageMSG = (this.recentlyViewedImagesMode && this.recentlyViewedImages?.length === 0) ? 'Images you\'ve recently viewed will appear here.' : this.noImageMSG;
    this.initializeCarousel();
  }

  ngAfterViewInit(): void {
    if (this.carousel) {
      this.carousel.nativeElement.addEventListener('pointermove', this.move, false);
      this.carousel.nativeElement.addEventListener('pointerdown', this.startDraggingCarousel, false);
      this.carousel.nativeElement.addEventListener('pointerup', this.stopDraggingCarousel, false);
      this.carousel.nativeElement.addEventListener('pointerleave', this.stopDraggingCarousel, false);
    } else {
      setTimeout(() => {
        this.carousel.nativeElement.addEventListener('pointermove', this.move, false);
        this.carousel.nativeElement.addEventListener('pointerdown', this.startDraggingCarousel, false);
        this.carousel.nativeElement.addEventListener('pointerup', this.stopDraggingCarousel, false);
        this.carousel.nativeElement.addEventListener('pointerleave', this.stopDraggingCarousel, false);
      }, 3000);
    }
  }

  startDraggingCarousel = (e) => {
    this.dragScrolling = true;
    this.startPosition = e.pageX;
    this.scrollDistance = this.carousel.nativeElement.scrollLeft;
  };

  stopDraggingCarousel = (e) => {
    this.dragScrolling = false;
  };

  move = (e) => {
    e.preventDefault();
    if (!this.dragScrolling) {
      return;
    }
    const x = e.pageX;
    const scroll = x - this.startPosition;
    this.carousel.nativeElement.scrollLeft = this.scrollDistance - scroll;
    if (this.carousel.nativeElement.scrollLeft > this.carousel.nativeElement.scrollWidth - (this.carousel.nativeElement.scrollWidth * 0.4)) {
      if (this.imageCarouselArray.length >= this.carouselSize - 70) {
        this.handleEndOfImageSet.emit();
        this.carouselSize += 35;
      }
      this.handleCarouselPagination('init');
    }
  };

  handleCarouselPagination(e) {
    if (e === 'forward') {
      this.carousel.nativeElement.scrollLeft += 250;
      if (this.carousel.nativeElement.scrollLeft > this.carousel.nativeElement.scrollWidth - (this.carousel.nativeElement.scrollWidth * 0.4)) {
      if (this.imageCarouselArray.length >= this.carouselSize - 70) {
        this.handleEndOfImageSet.emit();
        this.carouselSize += 35;
      }
        this.handleCarouselPagination('init');
      }
    } else if (e === 'back') {
      this.carousel.nativeElement.scrollLeft -= 250;
      if (this.carousel.nativeElement.scrollLeft > this.carousel.nativeElement.scrollWidth - (this.carousel.nativeElement.scrollWidth * 0.4)) {
      if (this.imageCarouselArray.length >= this.carouselSize - 40) {
        this.handleEndOfImageSet.emit();
        this.carouselSize += 35;
      }
        this.handleCarouselPagination('init');
      }
    } else if (e === 'init') {
      this.imageCarouselFirstIndex = 0;
      this.imageCarouselLastIndex = this.carouselSize;
      this.imageCarouselArray = this.mainDataContainer.slice(this.imageCarouselFirstIndex, this.imageCarouselLastIndex);
      // this.cd.markForCheck();
    }
    if (this.imageCarouselLastIndex === (this.mainDataContainer.length - 1)) {
      this.handleEndOfImageSet.emit();
    }
  }

  startHoldScroll(e) {
    this.holdScrolling = true;
    if (e === 'forward') {
      this.scrollCarousel(10)
      this.originalScroll = 10;
    } else if (e === 'back') {
      this.scrollCarousel(-10)
      this.originalScroll = -10;
    }
  }

  scrollCarousel(amount) {
    if (this.holdScrolling) {
      setTimeout(() => {
        this.carousel.nativeElement.scrollLeft += amount;
        if (this.carousel.nativeElement.scrollLeft > this.carousel.nativeElement.scrollWidth - (this.carousel.nativeElement.scrollWidth * 0.4)) {
          if (this.imageCarouselArray.length >= this.carouselSize - 70) {
            this.handleEndOfImageSet.emit();
            this.carouselSize += 35;
          }
          this.handleCarouselPagination('init');
        }
        this.scrollCarousel(Math.abs(amount) < Math.abs(this.originalScroll) * 5 ? amount + amount * 0.01 : amount);
      }, 50)
    }
  }

  stopHoldScroll() {
    this.holdScrolling = false;
    this.originalScroll = 0;
  }

  goToNextImage() {
    if (this.currentSelectedImageIndex == this.mainDataContainer.length - 1) {
      return;
    }
    if (this.currentSelectedImageIndex != this.mainDataContainer.length) {
        this.currentSelectedImageIndex++;
        //TODO: UPDATE TO USE RelatedImageSetViewDataModel.Relatedsetuid or find a way to clarify
        this.recordSelected.emit(this.mainDataContainer[this.currentSelectedImageIndex].uid);
    }
    if (!this.isHandset) {
      this.handleCarouselPagination('forward');
    }
  }

  goToPrevImage() {
    if (this.currentSelectedImageIndex == 0) {
      return;
    }
    if (this.currentSelectedImageIndex != 0) {
      this.currentSelectedImageIndex--;
      //TODO: UPDATE TO USE RelatedImageSetViewDataModel.Relatedsetuid or find a way to clarify 
      this.recordSelected.emit(this.mainDataContainer[this.currentSelectedImageIndex].uid);
    }
    if (!this.isHandset) {
      this.handleCarouselPagination('back');
    }
  }

  handleSelectImageCarouselThumbnail(data) {
    this.recordSelected.emit(this.isBoundImageSet || this.isLedgerSet? data.uid : data);
  }

  public triggerUpdate(): void {
    // eslint-disable-next-line max-len
    this.imageCarouselArray = this.isRSVMode? this.rsvImageModels : this.recentlyViewedImagesMode ? this.recentlyViewedImages : this.imageModels;
    this.cd.detectChanges();
  }

  // eslint-disable-next-line max-len
  /** Used to initialize Carousel either centered on selected image or if no image is selected at the start of the provided array of images */
  public initializeCarousel() {
    // eslint-disable-next-line max-len
    this.mainDataContainer = this.isRSVMode? this.rsvImageModels : this.recentlyViewedImagesMode ? this.recentlyViewedImages : this.imageModels;
    if (this.selectedImage) {
      this.displayName = this.selectedImage.name;
      const index = this.mainDataContainer.findIndex(x => x.name === this.displayName);
      if (index !== -1) {
        this.currentSelectedImageIndex = index;
        this.imageCarouselFirstIndex = index - Math.floor(this.carouselSize / 2) >= 0 ? index - Math.floor(this.carouselSize / 2) : 0;
        // eslint-disable-next-line max-len
        this.imageCarouselLastIndex = index - Math.floor(this.carouselSize / 2) >= 0 ? (index + Math.ceil(this.carouselSize / 2 - 1) <= this.imageModels.length - 1 ? index + Math.ceil(this.carouselSize / 2 - 1) : index) : index + this.carouselSize - 1;
        this.imageCarouselArray = this.mainDataContainer.slice(this.imageCarouselFirstIndex, this.imageCarouselLastIndex);
      } else {
        this.handleCarouselPagination('init');
      }
    } else {
      this.handleCarouselPagination('init');
    }
  }
}
